import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { toast } from "react-toastify";
import Layout from "../layouts/layout";
import Loader from "../components/loader";
import CreateReminderModal from "../components/createReminderModal";
import REMINDERS from "../api/services/REMINDERS";
import { paths } from "../routes/paths";
import styles from "../styles/_screens/reminders.module.scss";

const TABS = [
  { key: "all", label: "הכל" },
  { key: "active", label: "פעיל" },
  { key: "completed", label: "הושלם" },
  { key: "triggered", label: "ממתין" },
];

const RECURRENCE_LABELS = {
  none: "ללא",
  daily: "יומי",
  weekly: "שבועי",
  monthly: "חודשי",
};

const STATUS_LABELS = {
  active: "פעיל",
  triggered: "ממתין לטיפול",
  snoozed: "נדחה",
  completed: "הושלם",
  overdue: "עבר",
};

const STATUS_CLASSES = {
  active: styles.statusActive,
  triggered: styles.statusOverdue,
  snoozed: styles.statusOverdue,
  completed: styles.statusCompleted,
  overdue: styles.statusOverdue,
};

const SNOOZE_OPTIONS = [
  { label: "15 דקות", minutes: 15 },
  { label: "שעה", minutes: 60 },
  { label: "מחר", minutes: 1440 },
];

const InfoTooltip = ({ isOpen, onClose }) => {
  if (!isOpen) return null;
  return (
    <div className={styles.infoOverlay} onClick={onClose}>
      <div className={styles.infoCard} onClick={(e) => e.stopPropagation()}>
        <div className={styles.infoHeader}>
          <h3>מה זה תזכורות?</h3>
          <button className={styles.infoClose} onClick={onClose}>&times;</button>
        </div>
        <p className={styles.infoText}>
          תזכורות עוזרות לך לא לשכוח דברים חשובים. פשוט כתוב בטקסט חופשי מה ומתי — והמערכת תדאג להזכיר לך.
        </p>
        <p className={styles.infoSubtitle}>לדוגמה:</p>
        <ul className={styles.infoExamples}>
          <li>לבדוק את המצב של שרה בעוד שבוע</li>
          <li>לוודא שדני מילא את הטופס</li>
          <li>לחדש רישיון מקצועי בעוד חודש</li>
        </ul>
        <p className={styles.infoText}>
          אפשר לקשר תזכורת ללקוח ולהגדיר חזרה. תזכורות פעילות מופיעות גם בדף הראשי.
        </p>
        <div className={styles.infoTip}>
          <span>💡</span>
          <span>טיפ: השתמש בתזכורות למעקב אחרי לקוחות — למשל, בדיקת התקדמות חודש אחרי פגישה אחרונה.</span>
        </div>
      </div>
    </div>
  );
};

const Reminders = () => {
  const navigate = useNavigate();
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");
  const [search, setSearch] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [snoozeOpen, setSnoozeOpen] = useState(null);
  const [showInfo, setShowInfo] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);

  const fetchReminders = useCallback(async () => {
    setLoading(true);
    try {
      const data = await REMINDERS.get_reminders(activeTab);
      const list = Array.isArray(data)
        ? data
        : Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data?.reminders)
        ? data.reminders
        : [];
      setReminders(list);
    } catch (err) {
      console.log(err);
      toast.error("שגיאה בטעינת התזכורות");
    }
    setLoading(false);
  }, [activeTab]);

  useEffect(() => {
    fetchReminders();
  }, [fetchReminders]);

  // Close snooze dropdown on outside click
  useEffect(() => {
    const handleClick = () => setSnoozeOpen(null);
    if (snoozeOpen !== null) {
      document.addEventListener("click", handleClick);
    }
    return () => document.removeEventListener("click", handleClick);
  }, [snoozeOpen]);

  const handleModalClose = (created) => {
    setIsModalOpen(false);
    if (created) fetchReminders();
  };

  const handleComplete = async (id) => {
    setActionLoading(id + "_complete");
    try {
      await REMINDERS.complete_reminder(id);
      toast.success("תזכורת סומנה כבוצע");
      fetchReminders();
    } catch (err) {
      toast.error(err?.response?.data?.message || "שגיאה בעדכון התזכורת");
    }
    setActionLoading(null);
  };

  const handleSnooze = async (id, minutes) => {
    setSnoozeOpen(null);
    setActionLoading(id + "_snooze");
    try {
      const snoozeUntil = new Date(Date.now() + minutes * 60 * 1000).toISOString();
      await REMINDERS.snooze_reminder(id, { snoozeUntil });
      toast.success("התזכורת נדחתה");
      fetchReminders();
    } catch (err) {
      toast.error(err?.response?.data?.message || "שגיאה בדחיית התזכורת");
    }
    setActionLoading(null);
  };

  const handleDelete = async (id) => {
    setActionLoading(id + "_delete");
    try {
      await REMINDERS.delete_reminder(id);
      toast.success("התזכורת נמחקה");
      setReminders((prev) => prev.filter((r) => r._id !== id));
    } catch (err) {
      toast.error(err?.response?.data?.message || "שגיאה במחיקת התזכורת");
    }
    setActionLoading(null);
  };

  const filtered = reminders.filter((r) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      r.title?.toLowerCase().includes(q) ||
      r.clientName?.toLowerCase().includes(q)
    );
  });

  return (
    <Layout>
      <div className={styles.container} dir="rtl">
        {/* Header */}
        <div className={styles.pageHeader}>
          <div className={styles.pageTitleRow}>
            <h1 className={styles.pageTitle}>תזכורות</h1>
            <button className={styles.infoBtn} onClick={() => setShowInfo(true)} title="מה זה תזכורות?">?</button>
          </div>
          <button
            className={styles.createBtn}
            onClick={() => setIsModalOpen(true)}
          >
            + תזכורת חדשה
          </button>
        </div>

        <InfoTooltip isOpen={showInfo} onClose={() => setShowInfo(false)} />

        {/* Tabs */}
        <div className={styles.tabs}>
          {TABS.map((tab) => (
            <button
              key={tab.key}
              className={`${styles.tab} ${activeTab === tab.key ? styles.tabActive : ""}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Search */}
        <div className={styles.searchRow}>
          <input
            className={styles.searchInput}
            dir="rtl"
            type="text"
            placeholder="חיפוש לפי כותרת או לקוח..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {/* Content */}
        {loading ? (
          <div className={styles.loaderWrap}>
            <Loader />
          </div>
        ) : filtered.length === 0 ? (
          <div className={styles.emptyState}>
            <p className={styles.emptyIcon}>🔔</p>
            <p className={styles.emptyText}>
              {search ? "לא נמצאו תזכורות לחיפוש זה" : "אין תזכורות להצגה"}
            </p>
            {!search && (
              <button
                className={styles.createBtn}
                onClick={() => setIsModalOpen(true)}
              >
                צור תזכורת ראשונה
              </button>
            )}
          </div>
        ) : (
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>כותרת</th>
                  <th>תאריך</th>
                  <th>לקוח</th>
                  <th>חזרות</th>
                  <th>סטטוס</th>
                  <th>פעולות</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((reminder) => (
                  <tr key={reminder._id} className={styles.row}>
                    <td className={styles.titleCell}>{reminder.title}</td>
                    <td className={styles.dateCell}>
                      {reminder.dueDate
                        ? format(new Date(reminder.dueDate), "dd/MM/yyyy HH:mm")
                        : "—"}
                    </td>
                    <td className={styles.clientCell}>
                      {reminder.clientId ? (
                        <button
                          className={styles.clientLink}
                          onClick={() =>
                            navigate(paths.clientMemberShip(reminder.clientId?._id || reminder.clientId))
                          }
                        >
                          {reminder.clientId?.name || reminder.clientName || "—"}
                        </button>
                      ) : (
                        <span className={styles.noClient}>—</span>
                      )}
                    </td>
                    <td>
                      {RECURRENCE_LABELS[reminder.recurrence?.type || reminder.recurrence] || "ללא"}
                    </td>
                    <td>
                      <span
                        className={`${styles.statusBadge} ${
                          STATUS_CLASSES[reminder.status] || styles.statusActive
                        }`}
                      >
                        {STATUS_LABELS[reminder.status] || reminder.status || "פעיל"}
                      </span>
                    </td>
                    <td>
                      <div className={styles.rowActions}>
                        {/* Complete */}
                        {reminder.status !== "completed" && (
                          <button
                            className={`${styles.rowBtn} ${styles.completeBtn}`}
                            onClick={() => handleComplete(reminder._id)}
                            disabled={actionLoading === reminder._id + "_complete"}
                          >
                            בוצע
                          </button>
                        )}

                        {/* Snooze */}
                        {reminder.status !== "completed" && (
                          <div
                            className={styles.snoozeWrap}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button
                              className={`${styles.rowBtn} ${styles.snoozeBtn}`}
                              onClick={() =>
                                setSnoozeOpen(
                                  snoozeOpen === reminder._id
                                    ? null
                                    : reminder._id
                                )
                              }
                              disabled={actionLoading === reminder._id + "_snooze"}
                            >
                              דחה
                            </button>
                            {snoozeOpen === reminder._id && (
                              <div className={styles.snoozeDropdown}>
                                {SNOOZE_OPTIONS.map((opt) => (
                                  <button
                                    key={opt.minutes}
                                    className={styles.snoozeOption}
                                    onClick={() =>
                                      handleSnooze(reminder._id, opt.minutes)
                                    }
                                  >
                                    {opt.label}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        )}

                        {/* Delete */}
                        <button
                          className={`${styles.rowBtn} ${styles.deleteBtn}`}
                          onClick={() => handleDelete(reminder._id)}
                          disabled={actionLoading === reminder._id + "_delete"}
                        >
                          מחק
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal */}
      {isModalOpen && (
        <CreateReminderModal
          isOpen={isModalOpen}
          onClose={handleModalClose}
        />
      )}
    </Layout>
  );
};

export default Reminders;
